// ═══════════════════════════════════════════════════════════════════════════
// Componente raiz
// Extraído VERBATIM de index.html v0.9.16.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { APP_VERSION } from "./config/semilla.js";
import { clearStoredHandle } from "./datos/archivo.js";
import { migrateInvoices, migrateProducts } from "./datos/migraciones.js";
import { getFacturapiConfig } from "./fiscal/facturapi.js";
import { LOGO_BASE64 } from "./pdf/logo.js";
import { btnPrimary } from "./ui/estilos.js";
import { ClientAccounts } from "./ui/ClientAccounts.jsx";
import { ClientManager } from "./ui/ClientManager.jsx";
import { InvoiceCreator } from "./ui/InvoiceCreator.jsx";
import { ajusteDeStock } from "./dominio/remisiones.js";
import { salir, etiquetaRol } from "./auth/sesion.js";
import { puedeMembresia } from "./auth/permisos.js";
import { fuenteDeDatos, leerCatalogo } from "./datos/lectura.js";
import { encolar } from "./datos/bitacora.js";
import { clienteRPC, productoHaciaPG } from "./datos/adaptadores.js";
import { iniciarSincronizacion, programarSincronizacion } from "./datos/sincronizador.js";
import { IndicadorBitacora } from "./ui/EstadoBitacora.jsx";
import { Bodegas } from "./ui/Bodegas.jsx";
import { guardarRemision, guardarEdicion } from "./datos/remisiones-pg.js";
import { alAsignarFolio } from "./datos/sincronizador.js";
import { instalarMapas } from "./dominio/precios.js";
import { InvoiceHistory } from "./ui/InvoiceHistory.jsx";
import { MisVentas } from "./ui/MisVentas.jsx";
import { VisorDocumento } from "./ui/VisorDocumento.jsx";
import { ProductManager } from "./ui/ProductManager.jsx";
import { Settings } from "./ui/Settings.jsx";
import { Vendedores } from "./ui/Vendedores.jsx";
import { MiCuenta } from "./ui/MiCuenta.jsx";
import { tabsPara, tabPermitida } from "./ui/utiles.js";
import { pendientes as pendientesDePrecio } from "./datos/precios-solicitud.js";
import { estadoLlaveFiscal } from "./fiscal/timbrado-servidor.js";
import { BarraSuplantacion } from "./ui/BarraSuplantacion.jsx";
import { Plataforma } from "./ui/Plataforma.jsx";
import { supabase } from "./auth/sesion.js";

export function App({ sesion, membresia }) {
  const [tab, setTab] = useState("invoices");
  // Precarga del editor desde el historial (editar/duplicar) — lote 2.5
  const [preload, setPreload] = useState(null);
  const abrirEnEditor = (invoice, modo) => {
    setPreload({ invoice, modo, ts: Date.now() });   // ts fuerza re-disparo del efecto
    setTab("invoices");
  };
  // ── Arranca VACÍA, no con la semilla ─────────────────────────────────────
  //
  // INITIAL_CLIENTS son los 121 clientes REALES de la vinícola, incrustados en
  // el código desde el monolito. Mientras el dispositivo era la fuente eso
  // servía para arrancar en frío; ahora es un problema doble:
  //
  //   · el vendedor abría la app y veía 121 clientes y el folio 375 —datos que
  //     no son suyos ni están al día— hasta que Postgres respondiera. Eso es lo
  //     que el operador reportó como "la vista del vendedor no está
  //     sincronizada, tiene la remisión 375".
  //   · esos clientes viajan en el bundle de TODOS, incluidos los de otras
  //     vinícolas el día que existan.
  //
  // Con Postgres como fuente única no hay nada que sembrar: si no hay datos
  // todavía, lo honesto es decir que se están cargando.
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [folio, setFolio] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [fpiStatus, setFpiStatus] = useState(() => getFacturapiConfig());

  // `lastSync` se va con ellos: solo lo escribían las dos funciones muertas, así
  // que nunca volvía a cambiar. Un estado que no cambia es una dependencia que
  // miente sobre por qué se vuelve a correr un efecto.
  const dataRef = useRef({ clients: [], products: [], invoices: [], folio: null });

  // ¿Esta sesión es del superusuario de PLATAFORMA? No cuelga de la membresía
  // —el superusuario no tiene ninguna, y esa es la propiedad que le impide
  // curiosear datos— así que se pregunta aparte.
  const [esPlataforma, setEsPlataforma] = useState(false);
  useEffect(() => {
    let vivo = true;
    supabase.rpc("es_super_admin").then(({ data }) => { if (vivo) setEsPlataforma(!!data); });
    return () => { vivo = false; };
  }, [loaded]);

  // Keep refs in sync
  const lastModifiedRef = useRef(new Date().toISOString());
  useEffect(() => {
    if (loaded) {
      lastModifiedRef.current = new Date().toISOString();
      try { localStorage.setItem("vt-data-modified", lastModifiedRef.current); } catch {}
    }
    dataRef.current = { clients, products, invoices, folio, _lastModified: lastModifiedRef.current };
  }, [clients, products, invoices, folio, loaded]);
  // Refresh Facturapi status from localStorage
  useEffect(() => { setFpiStatus(getFacturapiConfig()); }, [loaded]);

  // Cuando el sincronizador consigue folio para una remisión que esperaba,
  // se refleja en pantalla sin que el usuario tenga que hacer nada.
  useEffect(() => alAsignarFolio(({ localId, folio: nuevo, datos }) => {
    if (!localId || nuevo == null) return;
    setInvoices(prev => prev.map(i => i.id === localId ? {
      ...i, folio: nuevo, _pendienteFolio: false,
      ...(datos ? {
        subtotal: Number(datos.subtotal), iepsAmount: Number(datos.ieps_amount),
        ivaAmount: Number(datos.iva_amount), total: Number(datos.total),
      } : {}),
    } : i));
    setFolio(f => (nuevo >= f ? nuevo + 1 : f));
  }), []);

  // ── Sincronización automática de la bitácora (Etapa 4b) ──
  // Sin botón: sube sola al arrancar, al volver la red y cada pocos minutos.
  useEffect(() => {
    if (!membresia?.tenant_id) return;
    return iniciarSincronizacion();
  }, [membresia?.tenant_id]);

  // Encola los cambios de catálogo. Se guarda en local SIEMPRE (el camino de
  // siempre); esto solo añade la mutación para subirla después.
  const encolarCatalogo = useCallback((tabla, filas, haciaPG) => {
    const t = membresia?.tenant_id;
    if (!t || fuenteDeDatos() !== "postgres") return;
    try {
      for (const fila of filas)
        encolar({ tabla, operacion: "upsert", clave: fila.id, datos: haciaPG(fila, t) });
      programarSincronizacion();
    } catch (e) {
      console.warn("[bitácora] no se pudo encolar:", e?.message);
    }
  }, [membresia?.tenant_id]);

  // Envoltorios: guardan como siempre y además encolan lo que cambió.
  // El cliente NO va por el upsert genérico: su lista de precios es una FK y
  // sus precios especiales viven en otra tabla. Sin esto, cambiar de lista o
  // poner un precio especial se veía aplicado y volvía atrás al recargar.
  const setClientsYEncolar = useCallback((accion) => {
    setClients(prev => {
      const nuevos = typeof accion === "function" ? accion(prev) : accion;
      const antes = new Map(prev.map(c => [c.id, JSON.stringify(c)]));
      const cambiados = nuevos.filter(c => antes.get(c.id) !== JSON.stringify(c));
      const t = membresia?.tenant_id;
      if (cambiados.length && t && fuenteDeDatos() === "postgres") {
        try {
          for (const c of cambiados)
            encolar({ tabla: "client", operacion: "guardar_cliente",
                      clave: c.id, datos: clienteRPC(c, t) });
          programarSincronizacion();
        } catch (e) {
          console.warn("[bitácora] no se pudo encolar el cliente:", e?.message);
        }
      }
      return nuevos;
    });
  }, [membresia?.tenant_id]);

  const setProductsYEncolar = useCallback((accion) => {
    setProducts(prev => {
      const nuevos = typeof accion === "function" ? accion(prev) : accion;
      const antes = new Map(prev.map(p => [p.id, JSON.stringify(p)]));
      const cambiados = nuevos.filter(p => antes.get(p.id) !== JSON.stringify(p));
      if (cambiados.length) encolarCatalogo("product", cambiados, productoHaciaPG);
      return nuevos;
    });
  }, [encolarCatalogo]);

  // ── Los datos, de Postgres ───────────────────────────────────────────────
  //
  // En su PROPIO efecto y sin condiciones de navegador. Antes colgaba del
  // efecto de arranque, después de un `return` que se dispara cuando no hay
  // API de archivos: Safari, Firefox y todo móvil se quedaban sin pedir nada
  // y con el aviso de carga puesto para siempre.
  const [carga, setCarga] = useState({ estado: "cargando", error: null });
  const [reintento, setReintento] = useState(0);

  useEffect(() => {
    let vivo = true;
    const aplicar = (d) => {
      if (!vivo) return;
      // Se aplica aunque venga VACÍO: un vendedor sin cartera tiene que ver
      // cero clientes, no los que arrastraba la pantalla.
      if (Array.isArray(d?.clients)) setClients(d.clients);
      if (Array.isArray(d?.products)) setProducts(migrateProducts(d.products));
      if (d?.pricingRules) {
        instalarMapas({
          vinopremier: d.pricingRules.vinopremier?.priceMap,
          sacacorchos: d.pricingRules.sacacorchos?.priceMap,
        });
      }
      if (Array.isArray(d?.invoices)) setInvoices(migrateInvoices(d.invoices));
      // Del CONTADOR, nunca de las remisiones visibles: el vendedor ve solo su
      // cartera y calcularlo de ahí anunciaría un folio ya usado.
      if (d?.siguienteFolio != null) setFolio(d.siguienteFolio);
      setCarga({ estado: "listo", error: null });
    };

    try {
      const { datos } = leerCatalogo({
        alActualizar: aplicar,
        // Un fallo SILENCIOSO se ve igual que «cargando». Se dice.
        alFallar: (e) => vivo && setCarga(c =>
          c.estado === "listo" ? c : { estado: "error", error: e?.message || "sin detalle" }),
      });
      if (datos) aplicar(datos);     // la caché manda: instantáneo, sin red
      else if (!navigator.onLine) setCarga({ estado: "sin-red", error: null });
    } catch (e) {
      setCarga({ estado: "error", error: e?.message || "sin detalle" });
    }
    return () => { vivo = false; };
  }, [reintento]);

  const sinDatosTodavia = carga.estado !== "listo" && folio === null && clients.length === 0;

  // ── Build the data snapshot for file ──
  const buildSnapshot = useCallback(() => ({
    _version: 2,
    _appVersion: APP_VERSION,
    _lastModified: new Date().toISOString(),
    _deviceId: localStorage.getItem("vt-device-id") || (() => { const id = "dev_" + Math.random().toString(36).substr(2, 8); localStorage.setItem("vt-device-id", id); return id; })(),
    folio: dataRef.current.folio,
    clients: dataRef.current.clients,
    products: dataRef.current.products,
    invoices: dataRef.current.invoices,
    facturapi: (() => { try { return JSON.parse(localStorage.getItem("vt-facturapi") || "null"); } catch { return null; } })(),
  }), []);

  // ── Write current state to file ──
  // ── La sincronización por archivo se RETIRÓ ───────────────────────────────
  //
  // Aquí vivían writeCurrentState, syncFromFile, mobileSyncAction,
  // connectToFile y disconnectFile: unas 200 líneas que mantenían un archivo
  // JSON en el disco como segunda copia de los datos.
  //
  // Tuvo sentido cuando el dispositivo era la fuente y había que pasar datos
  // de una máquina a otra. Desde 0.15.0 manda Postgres, y lo que quedó fue una
  // segunda verdad compitiendo con la primera. Cobró tres veces:
  //
  //   · dejó sin datos a Safari, Firefox y todo móvil (un `return` que las
  //     ramas sin API de archivos ejecutaban antes de pedir nada)
  //   · en el arranque, pisaba lo que traía Postgres cuando el archivo
  //     resolvía después — 2 remisiones de 46, y ninguna foto
  //   · y CADA 20 SEGUNDOS volvía a pisarlo, así que ni recargar servía:
  //     los datos buenos duraban hasta el siguiente tic
  //
  // Lo tercero es lo que dejaba a Cuentas con tres clientes de los 121.
  //
  // No se arregla ordenando la carrera: mientras existan dos copias, alguna
  // pierde. Se queda una. El trabajo sin red lo cubre `lectura.js` con su
  // caché sellada.
  //
  // ── Y con ellas se van sus dos últimos restos ──────────────────────────────
  //
  // `exportLocalData` e `importRemoteData` sobrevivieron a aquella limpieza
  // porque no rompían nada: nadie los llamaba. Settings los recibía como props
  // y no los usaba; no había un solo botón que llegara a ellos.
  //
  // Se van por dos razones, y la segunda pesa más que la higiene:
  //
  //   · Escribían un snapshot desde `dataRef` como si el dispositivo siguiera
  //     siendo la fuente. No lo es desde 0.15.0. Habrían resucitado la segunda
  //     verdad que costó tres incidentes.
  //   · `importRemoteData` hacía `localStorage.setItem("vt-facturapi", …)` y
  //     `exportLocalData` metía esa llave en el archivo descargado. Era EL
  //     ÚLTIMO camino por el que la llave fiscal podía volver al navegador
  //     después de haberla purgado y mandado al vault. Un camino muerto sigue
  //     siendo un camino: basta que alguien vuelva a cablear el botón.
  //
  // Respaldar hoy se hace con `herramientas/respaldar.py`, que lee de Postgres
  // —la fuente— y no de una copia del navegador.
  // ── Mobile sync: pick file → merge → download merged ──

  // ── Arranque: el archivo y el localStorage viejo YA NO CARGAN DATOS ───────
  //
  // ESTO ROMPIÓ PRODUCCIÓN, y de la peor manera: sin error, enseñando datos
  // que parecían buenos. El operador veía DOS remisiones de 46 y ninguna de
  // las fotos que estaban en la base.
  //
  // La causa: este efecto es asíncrono —handle de IndexedDB, permiso, leer el
  // archivo— y CORRÍA UNA CARRERA contra la carga de Postgres. El que
  // terminara al final ganaba. Cuando ganaba el archivo, pisaba las 46
  // remisiones y los 17 productos con un retrato viejo del dispositivo.
  //
  // No es una carrera que se pueda arreglar poniendo orden: son dos verdades,
  // y desde 0.15.0 solo hay una. Postgres manda, y quien guarda la copia para
  // trabajar sin red es `lectura.js` con su propia caché (`vt-pg-cache`), que
  // sí sabe cuándo está vieja.
  //
  // Lo que queda aquí es solo enterarse de si hay un archivo conectado, para
  // que Config lo refleje. No toca ni un dato.
  //
  // Es la §6 del handover —"retirar la sincronización por archivo"— cobrada
  // por segunda vez: ya había dejado sin datos a Safari, Firefox y todo móvil.
  useEffect(() => {
    (async () => {
      // Se SUELTA el archivo que hubiera quedado conectado de una versión
      // anterior. Si no, el navegador conserva el permiso y el handle queda
      // esperando a que alguien lo use — y ya vimos a qué lleva eso.
      try { await clearStoredHandle(); } catch { /* nada que soltar */ }

      // La carga de Postgres NO va aquí: vive en su propio efecto, más abajo.
      // Estaba dentro de este, DESPUÉS de un `return` que se ejecuta cuando el
      // navegador no soporta la API de archivos — o sea en Safari, Firefox y
      // TODO móvil. Ahí la app se quedaba en «Trayendo tus datos…» para
      // siempre, porque nunca llegaba a pedirlos.

      // El pull inicial de KV se retiró: Postgres es la fuente. Este bloque
      // fusionaba el snapshot remoto con el local, y ya no hay dos verdades
      // que reconciliar.

      setLoaded(true);
    })();
  }, []);

  // ── El respaldo en localStorage también se retira ─────────────────────────
  //
  // Nadie lo leía ya —el arranque dejó de hacerlo arriba— así que solo era una
  // segunda copia de la verdad esperando a que alguien la creyera. Que es
  // justo lo que acababa de pasar.
  //
  // La copia para trabajar sin red la lleva `lectura.js` en `vt-pg-cache`, con
  // su sello de tiempo. Ésta no lo tenía: no había manera de saber si lo que
  // guardaba era de hace un minuto o de hace un mes.
  //
  // Se limpian las claves viejas al arrancar, una vez, para que un dispositivo
  // que venga de 0.12.9 no se quede cargando 400 KB muertos para siempre.
  // Y de paso la LLAVE DE FACTURAPI. Se timbra desde el servidor desde hace
  // versiones; lo que queda en `vt-facturapi` es una llave que ya no se usa
  // durmiendo en el navegador de cada dispositivo, al alcance de cualquiera que
  // abra las herramientas de desarrollo. Es la deuda §9 del handover, y esta es
  // su mitad barata: sacarla de donde está.
  //
  // La otra mitad sigue pendiente y hay que decirlo: los caminos de importar y
  // de sincronizar por archivo TODAVÍA la vuelven a plantar si se usan, y la
  // llave live que se filtró el 3 de agosto sigue necesitando rotarse. Esto no
  // sustituye ninguna de las dos cosas.
  useEffect(() => {
    if (!loaded) return;
    for (const k of ["vt-clients", "vt-products", "vt-invoices", "vt-folio",
                     "vt-facturapi"]) {
      try { localStorage.removeItem(k); } catch { /* nada que hacer */ }
    }
  }, [loaded]);

  // El push automático a KV se retiró. Lo que cambia sube por la bitácora
  // (datos/sincronizador), que sabe QUÉ cambió en vez de reenviar el
  // snapshot entero cada vez.

  // El tic de 20 segundos que releía el archivo se retiró con el resto. Era el
  // que hacía que ni recargar sirviera: los datos buenos duraban un tic.

  // El pull periódico de KV se retiró: el refresco viene de Postgres, en
  // datos/lectura, que ahora trae remisiones además de catálogo.

  // ── Connect / reconnect to file ──


  const saveInvoice = (inv) => {
    // ── Editar: el mismo documento, corregido en el servidor (0064) ──
    //
    // Devuelve la remisión TAL COMO QUEDÓ en el servidor, o null si no se
    // pudo. Quien llama genera el PDF con eso: el documento que sale es el
    // guardado, nunca lo que había en pantalla. Si falla no se toca nada aquí.
    if (inv._editar) {
      const { _editar, ...limpia } = inv;
      if (fuenteDeDatos() !== "postgres" || !membresia?.tenant_id) {
        alert("❌ Editar una remisión necesita el servidor.");
        return Promise.resolve(null);
      }
      return guardarEdicion(limpia, membresia.tenant_id)
        .then(r => {
          aplicarRemisionGuardada(r.remision, limpia);
          if (r.aviso) alert("⚠️ " + r.aviso);
          return r.remision;
        })
        .catch(e => {
          alert("❌ No se guardó la corrección.\n\n" + (e?.message || e) +
                "\n\nLa remisión sigue como estaba.");
          return null;
        });
    }

    // ── Camino Postgres: el FOLIO lo asigna el servidor ──
    // Un consecutivo sin huecos ni repetidos necesita una sola autoridad. Sin
    // conexión la remisión se guarda igual, PENDIENTE DE FOLIO, y lo recibe al
    // sincronizar. Nunca se inventa un folio local: eso es lo que produjo el
    // duplicado del folio 376.
    if (fuenteDeDatos() === "postgres" && membresia?.tenant_id) {
      guardarRemision(inv, membresia.tenant_id).then(r => {
        aplicarRemisionGuardada(r.remision, inv);
        if (r.aviso) alert("⚠️ " + r.aviso);
        else if (r.pendiente) {
          alert(`Remisión guardada en este dispositivo.\n\n` +
                `Queda PENDIENTE DE FOLIO: el número lo asigna el servidor al ` +
                `sincronizar, para que no haya folios repetidos.` +
                (r.error ? `\n\nDetalle: ${r.error}` : ""));
        }
      });
      return;
    }

    aplicarRemisionGuardada(inv, inv);
  };

  /** Inserta o reemplaza la remisión en el estado y ajusta stock y folio. */
  const aplicarRemisionGuardada = (inv, original) => {
    // Se busca por id (identidad estable) y no por folio, que puede ser null
    // mientras espera al servidor.
    const existing = invoices.find(i => i.id === (original?.id || inv.id))
                  || invoices.find(i => inv.folio != null && i.folio === inv.folio);
    if (existing) {
      setInvoices(prev => prev.map(i => i.id === existing.id ? inv : i));
    } else {
      setInvoices(prev => [inv, ...prev]);
    }
    // El contador local solo avanza cuando el folio salió de aquí. Con Postgres
    // manda el servidor: el contador local se limita a no quedarse atrás.
    if (inv.folio != null && inv.folio >= folio) {
      setFolio(inv.folio + 1);
    }
    // Ajuste de stock por DELTA: si reemplaza una remisión existente, solo la
    // diferencia. Antes se descontaba completo otra vez (defecto corregido en 2.5).
    const delta = ajusteDeStock(existing || null, inv);
    if (delta.size > 0) {
      setProducts(prev => prev.map(p => {
        const d = delta.get(p.id);
        if (d) return { ...p, stock: Math.max(0, (p.stock || 0) - d) };
        return p;
      }));
    }
  };

  // ── El distintivo de la fuente ───────────────────────────────────────────
  //
  // Decía «Sin sincronizar» con el punto apagado, y era del sync POR ARCHIVO
  // que 0.15.0 dejó sin uso al volver Postgres la fuente. Como ya nadie conecta
  // un archivo, el letrero quedó clavado en un "no" permanente encima de datos
  // que SÍ estaban guardados: el aviso más alarmante de la pantalla, y falso.
  //
  // Ahora dice lo único que aquí importa: si la app está hablando con el
  // servidor. El sync por archivo sigue vivo en Config hasta que se retire
  // completo (§6 del handover); lo que se retira es su letrero en el encabezado.
  const FUENTE = {
    listo:     { dot: "#4ade80", label: "En línea",
                 ayuda: "Tus datos están en el servidor." },
    cargando:  { dot: "#facc15", label: "Cargando…",
                 ayuda: "Pidiéndole los datos al servidor." },
    "sin-red": { dot: "#facc15", label: "Sin red",
                 ayuda: "Sin conexión. Estás viendo la última copia de este dispositivo; lo que captures se sube cuando vuelva la red." },
    error:     { dot: "#f87171", label: "Sin conexión",
                 ayuda: "No se pudo hablar con el servidor. " + (carga.error || "") },
  };
  const fuente = FUENTE[carga.estado] || FUENTE.cargando;

  // Config no es de todos, así que tampoco lo son los atajos que llevan ahí.
  // Un distintivo que no responde al tocarlo confunde igual que uno que abre lo
  // que no debía.
  const puedoVerConfig = puedeMembresia(membresia, "ver_configuracion");
  const irAConfig = puedoVerConfig ? () => setTab("settings") : null;

  // La pestaña que se muestra, ya filtrada por rol. Ver `tabPermitida`.
  const tabActual = tabPermitida(tab, a => puedeMembresia(membresia, a), esPlataforma);

  // Cuántos precios esperan respuesta. Va en el encabezado porque una
  // solicitud sin contestar detiene una venta: si hay que acordarse de entrar a
  // Vendedores a mirar, se atora, y atorada devuelve al vendedor al teléfono.
  // El aviso de LIVE ya no puede salir de la llave local: se purgó, porque
  // timbra el servidor. Y perderlo sería un retroceso — es lo que distingue de
  // un vistazo el espejo de la vinícola de verdad. Se pregunta a la fuente.
  const [fiscalServidor, setFiscalServidor] = useState(null);
  useEffect(() => {
    const t = membresia?.tenant_id;
    if (!t) { setFiscalServidor(null); return; }
    let vivo = true;
    estadoLlaveFiscal(t).then(e => { if (vivo) setFiscalServidor(e); }).catch(() => {});
    return () => { vivo = false; };
  }, [membresia?.tenant_id]);

  const puedoAutorizarPrecios = puedeMembresia(membresia, "autorizar_precio");
  const [preciosPendientes, setPreciosPendientes] = useState(0);
  useEffect(() => {
    if (!puedoAutorizarPrecios || !membresia?.tenant_id) { setPreciosPendientes(0); return; }
    let vivo = true;
    const mirar = () => pendientesDePrecio(membresia.tenant_id)
      .then(n => { if (vivo) setPreciosPendientes(n); })
      .catch(() => {});   // callado: es un aviso, no una función
    mirar();
    const t = setInterval(mirar, 60000);
    return () => { vivo = false; clearInterval(t); };
  }, [puedoAutorizarPrecios, membresia?.tenant_id, tabActual]);

  if (!loaded) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#0a0a0a", color: "#c9a96e", fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🍷</div>
        <div style={{ fontSize: 20, letterSpacing: 4 }}>CARGANDO...</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#0a0a0a", color: "#e8e0d4", fontFamily: "'DM Sans', Helvetica, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet" />

      {/* Va ARRIBA del encabezado y es lo único que se permite estorbar: quien
          entra en la piel de otro para revisar una pantalla se distrae, y diez
          minutos después trabaja creyendo que es él mismo. */}
      <BarraSuplantacion onVolvi={() => window.location.reload()} />

      {/* Header */}
      <header style={{ borderBottom: "1px solid #1e1e1e", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "linear-gradient(180deg, #111 0%, #0a0a0a 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img src={LOGO_BASE64} alt="VT" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }} />
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: "#c9a96e", letterSpacing: 2 }}>VINÍCOLA TOTAL</div>
            <div style={{ fontSize: 10, color: "#666", letterSpacing: 3, marginTop: 2 }}>REMISIONES · CFDI 4.0</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#666", cursor: irAConfig ? "pointer" : "default", padding: "4px 10px", background: "#0d0d0d", borderRadius: 6, border: "1px solid #1e1e1e" }}
            onClick={irAConfig || undefined} title={fuente.ayuda}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: fuente.dot, boxShadow: `0 0 6px ${fuente.dot}` }} />
            <span>{fuente.label}</span>
          </div>
          {/* Con qué llave se timbra es asunto de la casa: al vendedor no le
              dice nada y lo llevaría a una pantalla que no es suya. */}
          {/* El rojo estaba al revés. El operador: "cfdi live aparece en rojo,
              ese color aparenta error; si está conectada y funcionando debe
              estar en verde."
              Tiene razón, y el semáforo estaba invertido: timbrar de verdad es
              el estado BUENO —es para lo que existe el sistema— y la llave de
              PRUEBAS es la anomalía, porque produce comprobantes que no valen
              ante el SAT. El ámbar va donde está el riesgo de confundirse. */}
          {fiscalServidor?.configurada && puedoVerConfig && (
            <div title={fiscalServidor.es_live
                  ? "Timbrando de verdad: los CFDI valen ante el SAT."
                  : "⚠️ Llave de PRUEBAS: lo que se timbre NO tiene validez fiscal."}
              style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: fiscalServidor.es_live ? "#4ade80" : "#facc15", cursor: "pointer", padding: "4px 10px", background: "#0d0d0d", borderRadius: 6, border: `1px solid ${fiscalServidor.es_live ? "#1a3a1a" : "#2a2a1a"}` }}
              onClick={irAConfig}>
              <span>🏛</span>
              <span>{fiscalServidor.es_live ? "CFDI" : "CFDI EN PRUEBAS"}</span>
            </div>
          )}
          {preciosPendientes > 0 && (
            <div onClick={() => setTab("team")} title="Precios que el equipo pidió y esperan tu respuesta."
              style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, cursor: "pointer",
                       padding: "4px 10px", background: "#1f1a0d", borderRadius: 6,
                       border: "1px solid #3a331a", color: "#facc15" }}>
              <span>✋</span>
              <span>{preciosPendientes} precio{preciosPendientes !== 1 ? "s" : ""} por autorizar</span>
            </div>
          )}
          <div style={{ fontSize: 12, color: "#555" }}>Folio: <span style={{ color: "#c9a96e" }}>{folio ?? "…"}</span></div>
          {/* Al vendedor SÍ le toca saber que su remisión no ha subido —es suya—
              pero el detalle vive en Config, así que para él solo informa. */}
          <IndicadorBitacora onAbrir={irAConfig} />
          {/* El distintivo "CATALOGO EN POSTGRES" se retiro: era de cuando
              habia dos modos y servia para saber en cual estabas. Ya no hay
              otra opcion, asi que solo decia algo que el usuario no puede
              cambiar ni necesita saber — y su tooltip todavia advertia que las
              ediciones no se guardaban alla, que hace dos versiones dejo de
              ser cierto. */}
          {sesion && (
            <div title={`${sesion.user?.email} · ${etiquetaRol(membresia?.role)}${membresia?.tenant?.name ? " · " + membresia.tenant.name : ""}`}
              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#888",
                padding: "4px 10px", background: "#0d0d0d", borderRadius: 6, border: "1px solid #1a1a1a" }}>
              <span style={{ color: "#c9a96e" }}>{(sesion.user?.email || "?").split("@")[0]}</span>
              <span style={{ color: "#444" }}>·</span>
              <span style={{ fontSize: 10 }}>{etiquetaRol(membresia?.role)}</span>
              <button onClick={async () => {
                if (!confirm("¿Cerrar sesión?\n\nTus datos siguen en este dispositivo; para volver a entrar necesitarás conexión.")) return;
                await salir();
              }} title="Cerrar sesión"
                style={{ background: "none", border: "none", color: "#666", cursor: "pointer",
                  padding: "0 0 0 4px", fontSize: 13 }}>⏻</button>
            </div>
          )}
          <div style={{ fontSize: 9, color: "#333" }}>v{APP_VERSION}</div>
        </div>
      </header>

      {/* Sin datos no hay app que usar: se tapa todo en vez de mostrar
          pestañas vacías que invitan a picarle. Y si falló, LO DICE — un
          fallo callado se ve idéntico a estar cargando, que fue justo lo que
          el operador vio durante minutos. */}
      {sinDatosTodavia && (
        <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "#0a0a0a",
                      display: "flex", flexDirection: "column", alignItems: "center",
                      justifyContent: "center", padding: 24, textAlign: "center" }}>
          <img src={LOGO_BASE64} alt="" style={{ width: 72, height: 72, objectFit: "contain",
                                                 opacity: carga.estado === "cargando" ? 0.9 : 0.35 }} />

          {carga.estado === "cargando" && (
            <>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24,
                            color: "#c9a96e", marginTop: 20 }}>
                Trayendo tus datos…
              </div>
              <div style={{ fontSize: 13, color: "#666", marginTop: 8, maxWidth: 340,
                            lineHeight: 1.7 }}>
                La primera vez en cada dispositivo se bajan tus clientes y tus
                remisiones. Después arranca al instante.
              </div>
            </>
          )}

          {carga.estado === "sin-red" && (
            <>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24,
                            color: "#facc15", marginTop: 20 }}>
                Sin conexión
              </div>
              <div style={{ fontSize: 13, color: "#888", marginTop: 8, maxWidth: 360,
                            lineHeight: 1.7 }}>
                Este dispositivo todavía no tiene tus datos guardados, así que hace
                falta internet una primera vez. Después funciona sin señal.
              </div>
            </>
          )}

          {carga.estado === "error" && (
            <>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24,
                            color: "#f87171", marginTop: 20 }}>
                No se pudieron traer tus datos
              </div>
              <div style={{ fontSize: 13, color: "#888", marginTop: 8, maxWidth: 380,
                            lineHeight: 1.7 }}>
                No se perdió nada: los datos están en el servidor. Vuelve a intentar,
                y si sigue igual avísale a quien administra la vinícola.
              </div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 10, maxWidth: 380,
                            fontFamily: "monospace", wordBreak: "break-word" }}>
                {carga.error}
              </div>
            </>
          )}

          {carga.estado !== "cargando" && (
            <button onClick={() => { setCarga({ estado: "cargando", error: null });
                                     setReintento(n => n + 1); }}
              style={{ ...btnPrimary, marginTop: 22, padding: "11px 26px", fontSize: 13 }}>
              Reintentar
            </button>
          )}

          <button onClick={() => salir()}
            style={{ marginTop: 14, padding: "8px 18px", fontSize: 12, background: "transparent",
                     border: "1px solid #222", borderRadius: 6, color: "#666", cursor: "pointer" }}>
            Cerrar sesión
          </button>
        </div>
      )}

      {/* Ver los documentos en vez de descargarlos a ciegas. Se monta una vez
          aquí: se engancha al único sitio por el que salen todos. */}
      <VisorDocumento />

      {/* Tab Nav */}
      <nav style={{ display: "flex", borderBottom: "1px solid #1a1a1a", background: "#0d0d0d", overflowX: "auto" }}>
        {tabsPara(a => puedeMembresia(membresia, a), esPlataforma).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: "14px 16px", border: "none", background: tabActual === t.id ? "#161616" : "transparent",
            color: tabActual === t.id ? "#c9a96e" : "#666", cursor: "pointer", fontSize: 13, fontWeight: 500,
            borderBottom: tabActual === t.id ? "2px solid #c9a96e" : "2px solid transparent",
            transition: "all 0.2s", whiteSpace: "nowrap", fontFamily: "inherit",
          }}>
            <span style={{ marginRight: 6 }}>{t.icon}</span>{t.label}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px" }}>
        <div style={{ display: tabActual === "invoices" ? "block" : "none" }}>
          <InvoiceCreator clients={clients} products={products} folio={folio} invoices={invoices} onSave={saveInvoice} preload={preload} membresia={membresia} setInvoices={setInvoices} onAbrirEnEditor={abrirEnEditor} />
        </div>
        {tabActual === "ventas" && <MisVentas clients={clients} products={products} invoices={invoices} setInvoices={setInvoices} membresia={membresia} onAbrirEnEditor={abrirEnEditor} />}
        {tabActual === "history" && <InvoiceHistory invoices={invoices} setInvoices={setInvoices} products={products} clients={clients} onAbrirEnEditor={abrirEnEditor} membresia={membresia} onIrAConfig={irAConfig} />}
        {tabActual === "accounts" && <ClientAccounts clients={clients} invoices={invoices} products={products} setInvoices={setInvoices} onIrAConfig={irAConfig} membresia={membresia} onAbrirEnEditor={abrirEnEditor} />}
        {tabActual === "clients" && <ClientManager clients={clients} setClients={setClientsYEncolar} products={products} invoices={invoices} setInvoices={setInvoices} membresia={membresia} onAbrirEnEditor={abrirEnEditor} onIrAConfig={irAConfig} />}
        {tabActual === "products" && <ProductManager products={products} setProducts={setProductsYEncolar} membresia={membresia} />}
        {tabActual === "team" && <Vendedores membresia={membresia} />}
        {tabActual === "me" && <MiCuenta sesion={sesion} membresia={membresia} />}
        {tabActual === "plataforma" && <Plataforma />}
        {tabActual === "bodegas" && <Bodegas products={products} membresia={membresia} />}
        {tabActual === "settings" && <Settings membresia={membresia} folio={folio} setFolio={setFolio} clients={clients} setClients={setClients} products={products} setProducts={setProducts} invoices={invoices} setInvoices={setInvoices} />}
      </main>
    </div>
  );
}
